import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_BASE } from '@/utils/constants';
import { useAuthStore } from '@/stores/authStore';
import type { ApiError } from '@/types';

/* ------------------------------------------------------------------ *
 * Idempotency key utilities
 * ------------------------------------------------------------------ */

export function generateIdempotencyKey(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Fallback (RFC4122-ish v4)
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/* ------------------------------------------------------------------ *
 * Axios instance
 * ------------------------------------------------------------------ */

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { Accept: 'application/json' },
  timeout: 30_000,
});

/* ---------- Request interceptor: idempotency key + auth header ---------- */

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (MUTATING.has(method)) {
    config.headers = config.headers ?? {};
    if (!config.headers['X-Idempotency-Key']) {
      config.headers['X-Idempotency-Key'] = generateIdempotencyKey();
    }
  }
  // Optionally mirror the cookie-backed access token into Authorization header.
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ---------- Response interceptor: silent 401 refresh ---------- */

let refreshPromise: Promise<void> | null = null;

async function attemptRefresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
      .then((res) => {
        const newToken: string | undefined = res.data?.accessToken;
        if (newToken) useAuthStore.getState().setAccessToken(newToken);
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        await attemptRefresh();
        return api.request(original);
      } catch (refreshErr) {
        const store = useAuthStore.getState();
        const wasAuthed = store.isAuthed || !!store.user || !!store.accessToken;
        if (wasAuthed) {
          store.reset();
          if (
            typeof window !== 'undefined' &&
            !window.location.pathname.startsWith('/auth')
          ) {
            const target = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.assign(`/auth?next=${target}`);
          }
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ *
 * Error parsing
 * ------------------------------------------------------------------ */

export function parseApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: ApiError } | undefined;
    if (data?.error) return data.error;
    return {
      code: err.code ?? 'NETWORK_ERROR',
      message: err.message || 'Network error',
    };
  }
  return { code: 'UNKNOWN', message: err instanceof Error ? err.message : 'Unknown error' };
}
